import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionJIECMonitoring = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    j1: "",
    j2: "",
    j3: "",
  });
  
  const yesNoMap = {
    [t.global.yes]: 1,
    [t.global.no]: 2,
  };
  
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations for all radio buttons
    if (!form.j1) newErrors.j1 = t.global.err_a1;
    if (!form.j2) newErrors.j2 = t.global.err_a1;
    if (!form.j3) newErrors.j3 = t.global.err_a1;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */
  // const handleSave = () => {
  //   if (!validateForm()) {
  //     // alert("Please fill in all required fields correctly before saving.");
  //     return;
  //   }

  //   try {
  //     // Save form data to localStorage
  //     const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
  //     surveyData.sectionJ = form;
  //     localStorage.setItem("surveyData", JSON.stringify(surveyData));

  //     // Mark Section J as completed
  //     const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
  //     completedSections.j = true;
  //     localStorage.setItem("completedSections", JSON.stringify(completedSections));

  //     // Navigate back to sections
  //     navigate("/survey/sections", { state });
      
  //     // Show success message
  //     alert("Section J saved successfully!");
  //   } catch (err) {
  //     console.error("Error saving data:", err);
  //     alert("Error saving data. Please try again.");
  //   }
  // };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const fd = new FormData();

      /* ================================
         LOCATION DATA
      ================================ */
      fd.append("district_id", state.district_id);
      fd.append("municipality_id", state.municipality_id);
      fd.append("ward_id", state.ward_id);
      fd.append("area_id", state.area_id);
      fd.append("canteen_type_id", state.canteen_type_id);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      /* ================================
         SECTION J – IEC & MONITORING
      ================================ */
      fd.append("iec_mon_J1", yesNoMap[form.j1]);
      fd.append("iec_mon_J2", yesNoMap[form.j2]);
      fd.append("iec_mon_J3", yesNoMap[form.j3]);

      /* ================================
         CURRENT SECTION
      ================================ */
      fd.append("current_section", "iec");

      /* ================================
         AUTO LAT / LNG
      ================================ */
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          fd.append("latitude", pos.coords.latitude);
          fd.append("longitude", pos.coords.longitude);

          /* ================================
             API CALL
          ================================ */
          await dispatch(createKitchenDining(fd));

          /* ================================
             LOCAL STORAGE ✅
          ================================ */
          const completedSections = JSON.parse(
            localStorage.getItem("completedSections") || "{}"
          );
          completedSections.j = true;
          localStorage.setItem(
            "completedSections",
            JSON.stringify(completedSections)
          );

          const surveyData = JSON.parse(
            localStorage.getItem("surveyData") || "{}"
          );
          surveyData.sectionJ = form;
          localStorage.setItem("surveyData", JSON.stringify(surveyData));

          
          navigate("/survey/sections", { state });
        },
        (err) => {
          console.error(err);
          alert(t.global.alert_missing_ids);
        }
      );
    } catch (err) {
      console.error("Section J submit error:", err);
      alert(t.global.alert_server_error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{t.modules.kitchenCumDining.sections.J.title}</h2>
              <p className="text-gray-600">{t.modules.kitchenCumDining.sections.J.subtitle}</p>
            </div>
            <div className="text-sm bg-orange-100 text-orange-800 px-3 py-1 rounded-full">
              {state?.canteenId ? `${t.global.canteen_id_label} ${state.canteenId}` : 
               state?.kioskId ? `${t.global.canteen_id_label} ${state.kioskId}` : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {/* J1 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                J.1 {t.modules.kitchenCumDining.sections.J.questions.posters} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label 
                    key={option} 
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.j1 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.j1 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="j1"
                      value={option}
                      checked={form.j1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <span className="text-lg">{option}</span>
                  </label>
                ))}
              </div>
              {errors.j1 && <p className="text-red-500 text-sm mt-2">{errors.j1}</p>}
            </div>

            {/* J2 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                J.2 {t.modules.kitchenCumDining.sections.J.questions.onlineEntry} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label 
                    key={option} 
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.j2 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.j2 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="j2"
                      value={option}
                      checked={form.j2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <span className="text-lg">{option}</span>
                  </label>
                ))}
              </div>
              {errors.j2 && <p className="text-red-500 text-sm mt-2">{errors.j2}</p>}
            </div>

            {/* J3 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                J.3 {t.modules.kitchenCumDining.sections.J.questions.couponReconciliation} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label 
                    key={option} 
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.j3 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.j3 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="j3"
                      value={option}
                      checked={form.j3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <span className="text-lg">{option}</span>
                  </label>
                ))}
              </div>
              {errors.j3 && <p className="text-red-500 text-sm mt-2">{errors.j3}</p>}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span> {t.global.err_complete_all}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_j}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionJIECMonitoring;